import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaCheck, FaCloudUploadAlt, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';

const STAGE_LABELS = {
  preparing: 'Preparing upload',
  compressing: 'Optimizing image',
  uploading: 'Uploading',
  processing: 'Processing media',
  ready: 'Upload complete',
  failed: 'Upload failed',
};

const getStageLabel = (uploadStatus) => {
  if (uploadStatus.error) return STAGE_LABELS.failed;
  if (uploadStatus.stage) return STAGE_LABELS[uploadStatus.stage] || STAGE_LABELS.uploading;
  if (uploadStatus.active) return `Uploading ${uploadStatus.type || 'media'}`;
  return STAGE_LABELS.ready;
};

const getHelperText = (uploadStatus) => {
  if (uploadStatus.error) return uploadStatus.error;
  if (uploadStatus.stage === 'preparing') return 'Creating a secure upload ticket. Keep this page open.';
  if (uploadStatus.stage === 'compressing') return 'Compressing the image so it uploads faster.';
  if (uploadStatus.stage === 'processing') return 'Finishing the file and making it playable.';
  if (uploadStatus.stage === 'failed') return 'You can retry from the same module.';
  if (uploadStatus.active) return uploadStatus.fileName || 'Keep this page open until upload finishes.';
  return 'Your media is ready.';
};

const getStatusIcon = (uploadStatus, theme) => {
  if (uploadStatus.error) return <FaExclamationTriangle style={{color: theme.stateError || '#B42318'}} />;
  if (uploadStatus.active) return <FaSpinner className="animate-spin" style={{color: theme.accent}} />;
  return <FaCheck style={{color: theme.stateSuccess || theme.accent}} />;
};

const UploadStatusOverlay = ({ uploadStatus = {}, theme = {} }) => {
  const visible = Boolean(uploadStatus.active || uploadStatus.error || uploadStatus.stage === 'ready');
  const progress = Math.max(0, Math.min(100, Math.round(uploadStatus.progress || 0)));
  const isError = Boolean(uploadStatus.error);
  const accent = isError ? (theme.stateError || '#B42318') : (theme.accent || '#9F1D22');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{opacity: 0, y: 22, scale: 0.98}}
          animate={{opacity: 1, y: 0, scale: 1}}
          exit={{opacity: 0, y: 22, scale: 0.98}}
          className="fixed inset-x-0 bottom-4 z-[10000] flex justify-center px-4 sm:bottom-6"
          data-html2canvas-ignore="true"
        >
          <div
            className="w-full max-w-md rounded-[1.35rem] border p-3 shadow-2xl backdrop-blur-xl"
            style={{
              backgroundColor: theme.cardBg || 'rgba(255,255,255,0.94)',
              borderColor: theme.cardBorder || 'rgba(232,221,210,0.95)',
              color: theme.text || '#211B1D',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                style={{backgroundColor: isError ? 'rgba(180,35,24,0.1)' : (theme.accentSoft || 'rgba(159,29,34,0.12)')}}
              >
                {getStatusIcon(uploadStatus, theme)}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className="truncate text-[10px] font-black uppercase tracking-widest">
                    {getStageLabel(uploadStatus)}
                  </p>
                  <span className="shrink-0 text-[10px] font-black" style={{color: accent}}>
                    {progress}%
                  </span>
                </div>

                <div className="mt-2 h-2 overflow-hidden rounded-full" style={{backgroundColor: theme.accentSoft || '#F6D9D6'}}>
                  <motion.div
                    className="h-full rounded-full"
                    initial={false}
                    animate={{width: `${progress}%`}}
                    transition={{duration: 0.35, ease: 'easeOut'}}
                    style={{backgroundColor: accent}}
                  />
                </div>

                <p className="mt-2 truncate text-[9px] font-bold uppercase tracking-widest" style={{color: theme.textMuted || theme.secondaryText || '#6F6561'}}>
                  {getHelperText(uploadStatus)}
                </p>
              </div>

              <div
                className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-full sm:flex"
                style={{backgroundColor: theme.inputBg || '#FFFDF8', color: accent}}
                aria-hidden="true"
              >
                <FaCloudUploadAlt />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UploadStatusOverlay;
